import { action, observable } from 'mobx'
import { mustBeInRange } from '@/utils/validx-util'
import { validationContext, required } from 'validx'
import { type InventoryItemPricingEditor } from './inventory-item-pricing-editor'
import {
  type InventoryItemMarkdownValues,
  type InventoryItemPricingValues,
} from '@/features/inventory/inventory-item-pricing-values'

/**
 * View Model for Inventory Items of type Markdown.
 */
export class MarkdownEditorViewModel implements InventoryItemPricingEditor {
  /**
   * The rate by which the job's price will be marked down.
   */
  @observable accessor clientMarkdownRate = 0

  /**
   * The rate by which the pro's payout will be marked down.
   */
  @observable accessor proPayoutMarkdownRate = 0

  /**
   * Validation context.
   */
  readonly validation = validationContext(this, {
    clientMarkdownRate: [
      required('A client markdown rate is required.'),
      mustBeInRange(0, 100, 'The rate must be between 0 and 100.'),
    ],

    proPayoutMarkdownRate: [
      required('A pro payout markdown rate is required.'),
      mustBeInRange(0, 100, 'The rate must be between 0 and 100.'),
    ],
  })

  /**
   * @inheritdoc
   */
  validate(): boolean {
    return this.validation.reset().validate().isValid
  }

  /**
   * @inheritdoc
   */
  @action.bound
  hydrate(values: InventoryItemMarkdownValues): void {
    this.clientMarkdownRate = values.clientMarkdownRate
    this.proPayoutMarkdownRate = values.proPayoutMarkdownRate
  }

  /**
   * @inheritdoc
   */
  toValues(): InventoryItemPricingValues {
    return {
      type: 'Markdown',
      clientMarkdownRate: this.clientMarkdownRate,
      proPayoutMarkdownRate: this.proPayoutMarkdownRate,
    }
  }

  /**
   * @inheritdoc
   */
  @action.bound
  reset() {
    this.clientMarkdownRate = 0
    this.proPayoutMarkdownRate = 0
    this.validation.reset()
  }
}
